import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import { logError } from '../utils/errorHandler';
// A cycle (impersonationApi imports `api` from here), and a safe one: neither
// module reads the other's exports while it is being evaluated — only inside
// functions that run long after both have loaded.
import { isImpersonating, exitImpersonation } from './impersonationApi';

/**
 * Read the CSRF token cookie set by the backend.
 * Production uses __Host-csrf (requires Secure); dev uses csrf-token.
 */
function getCsrfToken(): string | null {
  // Try production name first, then dev name
  const match =
    document.cookie.match(/(?:^|;\s*)__Host-csrf=([^;]+)/) ||
    document.cookie.match(/(?:^|;\s*)csrf-token=([^;]+)/);
  return match ? decodeURIComponent(match[1]) : null;
}

const api = axios.create({
  baseURL: '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

const MUTATING_METHODS = new Set(['post', 'put', 'patch', 'delete']);

// Ensure a CSRF cookie exists by making a lightweight GET if needed.
// The backend sets the cookie on any safe (GET/HEAD/OPTIONS) request.
let csrfInitPromise: Promise<void> | null = null;
async function ensureCsrfToken(): Promise<void> {
  if (getCsrfToken()) return;
  if (!csrfInitPromise) {
    csrfInitPromise = axios
      .get('/api/csrf-init', { timeout: 5000, withCredentials: true })
      .then(() => {})
      .catch(() => {})
      .finally(() => { csrfInitPromise = null; });
  }
  await csrfInitPromise;
}

// Force a fresh CSRF cookie even if one is already present — used to self-heal
// a stale/rejected token (e.g. after switching logins in the same browser).
async function forceCsrfRefresh(): Promise<void> {
  try { await axios.get('/api/csrf-init', { timeout: 5000, withCredentials: true }); } catch { /* non-fatal */ }
}

// Request interceptor — attach auth token and CSRF token
api.interceptors.request.use(
  async (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.method && MUTATING_METHODS.has(config.method.toLowerCase())) {
      // Ensure CSRF cookie exists before any mutating request
      await ensureCsrfToken();
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRF-Token'] = csrfToken;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// A request that may 401 without the page being sent to /login. For a PUBLIC
// page asking "is anyone signed in?" (PricingPage): an expired session there
// means "show the visitor's page", not "log in". The refresh still runs and a
// dead session is still cleared — only the redirect is skipped.
declare module 'axios' {
  interface AxiosRequestConfig { skipAuthRedirect?: boolean }
}

/**
 * B47 — keep the fresh pair the server hands back when it has just ended EVERY
 * session, this tab's included (a password change, 2FA turned off). Without
 * this the tab that made the change is signed out on its next click. A reply
 * with no pair leaves the stored one alone.
 */
export const adoptReissuedTokens = (tokens?: { accessToken?: string; refreshToken?: string } | null): void => {
  if (!tokens?.accessToken || !tokens.refreshToken) return;
  localStorage.setItem('token', tokens.accessToken);
  localStorage.setItem('refreshToken', tokens.refreshToken);
};

const redirectToLogin = (config?: { skipAuthRedirect?: boolean }): void => {
  if (!config?.skipAuthRedirect) window.location.href = '/login';
};

// Response interceptor for handling errors and token refresh
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Log the error
    logError(error, `API Request: ${originalRequest?.method?.toUpperCase()} ${originalRequest?.url}`);

    // B50 / N1 — A 401 WHILE IMPERSONATING ENDS THE IMPERSONATION; IT NEVER
    // REFRESHES. Exit, a revoked grant and an expired grant all answer 401
    // IMPERSONATION_ENDED, and this used to fall into the refresh below — which
    // turned the ended impersonation into a plain 30-day session AS THE TARGET.
    // Impersonations carry no refresh token any more (and the server refuses to
    // refresh one), so the only right move is back to the impersonator's own
    // session. Not awaited: exitImpersonation's own /impersonation/end call can
    // 401 into this same branch (see its note).
    if (error.response?.status === 401 && isImpersonating()) {
      void exitImpersonation();
      return Promise.reject(error);
    }

    // If error is 401 and we haven't tried to refresh yet
    if (error.response?.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If already refreshing, queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('refreshToken');

      if (!refreshToken) {
        // No refresh token, redirect to login
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        redirectToLogin(originalRequest);
        return Promise.reject(error);
      }

      try {
        // Attempt to refresh the token
        const response = await axios.post('/api/auth/refresh', {
          refreshToken,
        });

        if (response.data.success && response.data.data?.tokens) {
          // Backend returns a TokenPair: { accessToken, refreshToken }. Reading
          // `token` here (the old field name) stored `undefined`, so every
          // retry 401'd and bounced the user to login.
          const { accessToken: token, refreshToken: newRefreshToken } = response.data.data.tokens;

          // Update tokens in localStorage
          localStorage.setItem('token', token);
          localStorage.setItem('refreshToken', newRefreshToken);

          // Update the authorization header
          if (originalRequest.headers) {
            originalRequest.headers.Authorization = `Bearer ${token}`;
          }

          // Process queued requests
          processQueue(null, token);

          // Retry the original request
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed, clear tokens and redirect to login
        processQueue(refreshError, null);
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        redirectToLogin(originalRequest);
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // For other errors or if refresh failed
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('refreshToken');
      redirectToLogin(originalRequest);
    }

    // 403 CSRF failure — the token is missing/stale (commonly after switching
    // logins in the same browser). Force-refresh the CSRF cookie and retry the
    // request once, transparently, so the user never has to refresh manually.
    const csrfReq = error.config as (InternalAxiosRequestConfig & { _csrfRetry?: boolean }) | undefined;
    if (
      error.response?.status === 403
      && (error.response.data as { error?: { code?: string } } | undefined)?.error?.code === 'CSRF_INVALID'
      && csrfReq && !csrfReq._csrfRetry
    ) {
      csrfReq._csrfRetry = true;
      await forceCsrfRefresh();
      const fresh = getCsrfToken();
      if (fresh && csrfReq.headers) csrfReq.headers['X-CSRF-Token'] = fresh;
      return api(csrfReq);
    }

    // 423 Locked — user suspended (or their parent merchant is). Auth
    // middleware whitelists certain recovery paths so /account-suspended
    // and the wallet/pay-overdue endpoints stay reachable. Redirect to
    // /account-suspended ONCE per session (skip if already on that page).
    // Wave 1.1 Step 3.6 / 5b.
    if (
      error.response?.status === 423
      && typeof window !== 'undefined'
      && !window.location.pathname.startsWith('/account-suspended')
      && !window.location.pathname.startsWith('/access-blocked')
    ) {
      const body = error.response.data as { error?: { code?: string } } | undefined;
      const code = body?.error?.code;
      if (code === 'MERCHANT_SUSPENDED' || code === 'ACCOUNT_SUSPENDED') {
        // Merchant suspended (own) → recovery page. Customer whose parent
        // merchant is suspended (MERCHANT_SUSPENDED but the user is an
        // customer) → access-blocked page. The page itself reads /me to
        // distinguish.
        window.location.href = '/account-suspended';
      }
    }

    return Promise.reject(error);
  }
);

// ============================================================================
// API Service Functions
// ============================================================================

// Types for API responses
export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: {
    code: string;
    message: string;
    details?: unknown;
  };
  message?: string;
}

// ============================================================================
// Profile Management API
// ============================================================================

export interface UserProfile {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  company?: string;
  phone?: string;
  role: 'admin' | 'merchant' | 'customer';
  accountCredit: number;
  createdAt: string;
  updatedAt: string;
}

export interface ProfileUpdateData {
  email?: string;
  firstName?: string;
  lastName?: string;
  company?: string;
  phone?: string;
}

/**
 * Get current user's profile
 */
export const getProfile = async (): Promise<ApiResponse<{ user: UserProfile }>> => {
  const response = await api.get<ApiResponse<{ user: UserProfile }>>('/users/profile');
  return response.data;
};

/**
 * Update current user's profile
 */
export const updateProfile = async (data: ProfileUpdateData): Promise<ApiResponse<{ user: UserProfile }>> => {
  const response = await api.put<ApiResponse<{ user: UserProfile }>>('/users/profile', data);
  return response.data;
};

// ============================================================================
// User Management API (Merchant)
// ============================================================================

export interface Customer {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  company?: string;
  accountCredit: number;
  isFlagged: boolean;
  flagReason?: 'insufficient_credit' | 'overdue_billing';
  overdueAmount?: number;
  createdAt: string;
}

export interface CustomerDetail extends Customer {
  // `billingCycles` was removed 2026-07-29 with the billing-cycle register.
}

/**
 * Get all customers for the current merchant
 */
export const getCustomers = async (flaggedOnly?: boolean): Promise<ApiResponse<{ users: Customer[] }>> => {
  const params = flaggedOnly ? { flagged: 'true' } : {};
  const response = await api.get<ApiResponse<{ users: Customer[] }>>('/users/end-users', { params });
  return response.data;
};

/**
 * Get detailed information about a specific customer
 */
export const getCustomerDetail = async (userId: string): Promise<ApiResponse<{ user: CustomerDetail }>> => {
  const response = await api.get<ApiResponse<{ user: CustomerDetail }>>(`/users/end-users/${userId}`);
  return response.data;
};

// ============================================================================
// Admin User Management API
// ============================================================================

export interface AdminUser {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  company?: string;
  role: 'admin' | 'merchant' | 'customer';
  accountCredit: number;
  isFlagged: boolean;
  flagReason?: 'insufficient_credit' | 'overdue_billing';
  overdueAmount?: number;
  parentId?: string;
  createdAt: string;
}

export interface AdminUserDetail extends AdminUser {
  payments: Array<{
    id: string;
    amount: number;
    currency: string;
    status: string;
    paymentMethod: string;
    createdAt: string;
  }>;
}

export interface AdminUserFilters {
  role?: 'merchant' | 'customer';
  flagged?: boolean;
}

/**
 * Get all users (admin only)
 */
export const getAllUsers = async (filters?: AdminUserFilters): Promise<ApiResponse<{ users: AdminUser[] }>> => {
  const response = await api.get<ApiResponse<{ users: AdminUser[] }>>('/users/all', { params: filters });
  return response.data;
};

/**
 * Get detailed information about any user (admin only)
 */
export const getUserDetail = async (userId: string): Promise<ApiResponse<{ user: AdminUserDetail }>> => {
  const response = await api.get<ApiResponse<{ user: AdminUserDetail }>>(`/users/${userId}`);
  return response.data;
};

// ============================================================================
// Credit Management API
// ============================================================================

export interface CreditBalance {
  userId: string;
  accountCredit: number;
  currency: string;
}

export interface TopUpRequest {
  amount: number;
}

export interface TopUpResponse {
  paymentUrl: string;
  paymentId: string;
}

/**
 * Get current user's credit balance
 */
export const getCreditBalance = async (): Promise<ApiResponse<CreditBalance>> => {
  // The endpoint is GET /billing/credit and returns { data: { balance: number } }.
  // (Previously this hit /billing/credit/balance — a non-existent route — and
  // expected a different shape, so it 404'd silently.) Adapt to CreditBalance so
  // callers reading data.accountCredit keep working without touching the other
  // widgets that consume data.balance directly.
  const response = await api.get<ApiResponse<{ balance: number }>>('/billing/credit');
  return {
    success: response.data.success,
    data: response.data.data
      ? { userId: '', accountCredit: response.data.data.balance ?? 0, currency: 'ZAR' }
      : undefined,
    error: response.data.error,
  };
};

/**
 * Initiate credit top-up
 */
export const initiateTopUp = async (amount: number): Promise<ApiResponse<TopUpResponse>> => {
  const response = await api.post<ApiResponse<TopUpResponse>>('/billing/credit/topup', { amount });
  return response.data;
};

/**
 * Check if user has sufficient credit for a purchase
 */
export const checkSufficientCredit = async (amount: number): Promise<ApiResponse<{ sufficient: boolean }>> => {
  const response = await api.post<ApiResponse<{ sufficient: boolean }>>('/billing/credit/check', { amount });
  return response.data;
};

// ---- Chunk 4J additions ----

export interface CreditTransaction {
  id: string;
  userId: string;
  amount: number;
  transactionType: string;
  description: string;
  reference?: string;
  notes?: string;
  createdByUserId?: string;
  subscriptionPaymentId?: string;
  relatedPaymentId?: string;
  balanceBefore: number;
  balanceAfter: number;
  createdAt: string;
}

export interface ReceiptPaymentPayload {
  customerId:  string;
  amount:    number;
  method:    'eft' | 'cash' | 'payfast_adhoc';
  reference: string;
  notes?:    string;
}

/**
 * Merchant receipts a payment against a customer account.
 */
export const receiptPayment = async (
  data: ReceiptPaymentPayload
): Promise<ApiResponse<{ message: string }>> => {
  const response = await api.post<ApiResponse<{ message: string }>>('/billing/credit/receipt', data);
  return response.data;
};

/**
 * Get own credit transaction history (all roles).
 */
export const getMyTransactionHistory = async (
  limit = 50,
  offset = 0
): Promise<ApiResponse<{ transactions: CreditTransaction[]; total: number }>> => {
  const response = await api.get<ApiResponse<{ transactions: CreditTransaction[]; total: number }>>(
    '/billing/credit/transactions',
    { params: { limit, offset } }
  );
  return response.data;
};

/**
 * Get credit transaction history for a specific customer (merchant/admin).
 */
export const getCustomerTransactionHistory = async (
  userId: string,
  limit = 50,
  offset = 0
): Promise<ApiResponse<{ transactions: CreditTransaction[]; total: number }>> => {
  const response = await api.get<ApiResponse<{ transactions: CreditTransaction[]; total: number }>>(
    `/billing/credit/transactions/${userId}`,
    { params: { limit, offset } }
  );
  return response.data;
};

/**
 * Initiate a PayFast ad-hoc credit top-up (customer self-service).
 * Returns a PayFast redirect URL + form fields.
 */
export const initiateCreditTopUp = async (
  amount: number
): Promise<ApiResponse<{ paymentUrl: string; reference: string }>> => {
  const response = await api.post<ApiResponse<{ paymentUrl: string; reference: string }>>(
    '/billing/credit/topup',
    { amount, paymentMethod: 'payfast' }
  );
  return response.data;
};

// ============================================================================
// Payment Management API
// ============================================================================

export interface Payment {
  id: string;
  userId: string;
  userName?: string;
  userRole?: 'admin' | 'merchant' | 'customer';
  billingCycleId?: string;
  amount: number;
  currency: string;
  status: 'pending' | 'completed' | 'failed';
  paymentMethod: string;
  paymentGateway?: string;
  transactionId?: string;
  createdAt: string;
  completedAt?: string;
}

export interface PaymentFilters {
  userId?: string;
  billingCycleId?: string;
  userRole?: 'merchant' | 'customer';
  startDate?: string;
  endDate?: string;
  status?: 'pending' | 'completed' | 'failed';
}

/**
 * Get payment history with optional filters
 */
export const getPayments = async (filters?: PaymentFilters): Promise<ApiResponse<{ payments: Payment[] }>> => {
  const response = await api.get<ApiResponse<{ payments: Payment[] }>>('/billing/payments', { params: filters });
  return response.data;
};

/**
 * Get payments for a specific user (merchant/admin only)
 */
export const getPaymentsByUser = async (userId: string): Promise<ApiResponse<{ payments: Payment[] }>> => {
  const response = await api.get<ApiResponse<{ payments: Payment[] }>>('/billing/payments', {
    params: { userId },
  });
  return response.data;
};

// The Billing Cycle Management API was removed 2026-07-29 with the hosting-era
// charge register it drove. Note `CatalogBillingCycle` below is unrelated — it
// is the product catalogue's term length.

// ============================================================================
// Product Catalog API (Phase 1)
// ============================================================================

import type {
  ProductCatalog,
  ProductPricingTier,
  ProductFeature,
  ProductFilters,
  CatalogBillingCycle,
  ProductFeatureType,
  ProductOptionGroup,
  ProductOptionInputType,
  ProductAddonLink,
  ProductVariant,
} from '../types';

// --- Products ---

export const getProducts = async (filters: ProductFilters = {}): Promise<ApiResponse<ProductCatalog[]>> => {
  const response = await api.get<ApiResponse<ProductCatalog[]>>('/products', { params: filters });
  return response.data;
};

export const getProduct = async (id: string): Promise<ApiResponse<ProductCatalog>> => {
  const response = await api.get<ApiResponse<ProductCatalog>>(`/products/${id}`);
  return response.data;
};

export interface CreateProductPayload {
  name: string;
  product_type: string;
  category_id?: string | null;
  description?: string | null;
  auto_provision?: boolean;
  sort_order?: number;
  trial_period_days?: number;
  metadata?: Record<string, unknown>;
}

export const createProduct = async (data: CreateProductPayload): Promise<ApiResponse<ProductCatalog>> => {
  const response = await api.post<ApiResponse<ProductCatalog>>('/products', data);
  return response.data;
};

export interface UpdateProductPayload {
  name?: string;
  product_type?: string;
  category_id?: string | null;
  description?: string | null;
  is_active?: boolean;
  auto_provision?: boolean;
  sort_order?: number;
  trial_period_days?: number;
  metadata?: Record<string, unknown>;
}

export const updateProduct = async (id: string, data: UpdateProductPayload): Promise<ApiResponse<ProductCatalog>> => {
  const response = await api.put<ApiResponse<ProductCatalog>>(`/products/${id}`, data);
  return response.data;
};

export const deactivateProduct = async (id: string): Promise<ApiResponse<{ message: string }>> => {
  const response = await api.delete<ApiResponse<{ message: string }>>(`/products/${id}`);
  return response.data;
};

/** Permanently delete a product (guarded server-side; rejects if it has subscriptions). */
export const deleteProduct = async (id: string): Promise<ApiResponse<{ message: string }>> => {
  const response = await api.delete<ApiResponse<{ message: string }>>(`/products/${id}/permanent`);
  return response.data;
};

/** Duplicate a product into a new inactive draft (pricing + features copied). */
export const duplicateProduct = async (id: string): Promise<ApiResponse<ProductCatalog>> => {
  const response = await api.post<ApiResponse<ProductCatalog>>(`/products/${id}/duplicate`);
  return response.data;
};

// --- Pricing ---

export interface CreatePricingPayload {
  billing_cycle: CatalogBillingCycle;
  price: number;
  setup_fee?: number;
  currency?: string;
}

export const addProductPricing = async (productId: string, data: CreatePricingPayload): Promise<ApiResponse<ProductPricingTier>> => {
  const response = await api.post<ApiResponse<ProductPricingTier>>(`/products/${productId}/pricing`, data);
  return response.data;
};

export interface UpdatePricingPayload {
  price?: number;
  setup_fee?: number;
  currency?: string;
  is_active?: boolean;
}

export const updateProductPricing = async (productId: string, pricingId: string, data: UpdatePricingPayload): Promise<ApiResponse<ProductPricingTier>> => {
  const response = await api.put<ApiResponse<ProductPricingTier>>(`/products/${productId}/pricing/${pricingId}`, data);
  return response.data;
};

/** Remove a pricing tier — hard-deletes if unreferenced, else soft-deactivates. */
export const deleteProductPricing = async (productId: string, pricingId: string): Promise<ApiResponse<{ removed: boolean }>> => {
  const response = await api.delete<ApiResponse<{ removed: boolean }>>(`/products/${productId}/pricing/${pricingId}`);
  return response.data;
};

// --- Variations (R1 §5.2 — `variable` plans) ---
//
// A variation carries its own absolute prices, so pricing nests under the
// variation rather than sharing the plan-level pricing calls above. These map
// 1:1 onto the routes merchant variation CRUD has published since sub-chunk 3.

export interface CreateVariantPayload {
  sku: string;
  name?: string | null;
  attributes?: Record<string, unknown>;
  is_active?: boolean;
  sort_order?: number;
  /** B90 — null = the plan's trial, 0 = none, N = N days. */
  trial_period_days?: number | null;
}

export interface UpdateVariantPayload {
  sku?: string;
  name?: string | null;
  attributes?: Record<string, unknown>;
  is_active?: boolean;
  sort_order?: number;
  /** B90 — omitted leaves it; null sets it back to the plan's trial. */
  trial_period_days?: number | null;
}

/** Variations of a plan, each with its own pricing tiers attached. */
export const getProductVariants = async (productId: string): Promise<ApiResponse<{ variants: ProductVariant[] }>> => {
  const response = await api.get<ApiResponse<{ variants: ProductVariant[] }>>(`/products/${productId}/variants`);
  return response.data;
};

export const createProductVariant = async (productId: string, data: CreateVariantPayload): Promise<ApiResponse<ProductVariant>> => {
  const response = await api.post<ApiResponse<ProductVariant>>(`/products/${productId}/variants`, data);
  return response.data;
};

export const updateProductVariant = async (productId: string, variantId: string, data: UpdateVariantPayload): Promise<ApiResponse<ProductVariant>> => {
  const response = await api.put<ApiResponse<ProductVariant>>(`/products/${productId}/variants/${variantId}`, data);
  return response.data;
};

/** Delete a variation. Refused server-side once it has subscriptions — deactivate instead. */
export const deleteProductVariant = async (productId: string, variantId: string): Promise<ApiResponse<{ message: string }>> => {
  const response = await api.delete<ApiResponse<{ message: string }>>(`/products/${productId}/variants/${variantId}`);
  return response.data;
};

export const addVariantPricing = async (
  productId: string,
  variantId: string,
  data: CreatePricingPayload,
): Promise<ApiResponse<ProductPricingTier>> => {
  const response = await api.post<ApiResponse<ProductPricingTier>>(
    `/products/${productId}/variants/${variantId}/pricing`, data,
  );
  return response.data;
};

export const updateVariantPricing = async (
  productId: string,
  variantId: string,
  pricingId: string,
  data: UpdatePricingPayload,
): Promise<ApiResponse<ProductPricingTier>> => {
  const response = await api.put<ApiResponse<ProductPricingTier>>(
    `/products/${productId}/variants/${variantId}/pricing/${pricingId}`, data,
  );
  return response.data;
};

/** Remove a variation's pricing tier — hard-deletes if unreferenced, else soft-deactivates. */
export const deleteVariantPricing = async (
  productId: string,
  variantId: string,
  pricingId: string,
): Promise<ApiResponse<{ removed: boolean }>> => {
  const response = await api.delete<ApiResponse<{ removed: boolean }>>(
    `/products/${productId}/variants/${variantId}/pricing/${pricingId}`,
  );
  return response.data;
};

// --- Options (Wave 4.6 Catalog Depth) ---

export interface OptionPayload {
  label: string;
  priceDelta: number;
  setupFeeDelta?: number;
  isDefault?: boolean;
}

export interface OptionGroupPayload {
  name: string;
  inputType: ProductOptionInputType;
  isRequired: boolean;
  options: OptionPayload[];
}

export const getProductOptionGroups = async (
  productId: string,
): Promise<ApiResponse<{ optionGroups: ProductOptionGroup[] }>> => {
  const response = await api.get<ApiResponse<{ optionGroups: ProductOptionGroup[] }>>(
    `/products/${productId}/option-groups`,
  );
  return response.data;
};

/** Replace the product's full option-group set in a single call. */
export const replaceProductOptionGroups = async (
  productId: string,
  groups: OptionGroupPayload[],
): Promise<ApiResponse<{ optionGroups: ProductOptionGroup[] }>> => {
  const response = await api.put<ApiResponse<{ optionGroups: ProductOptionGroup[] }>>(
    `/products/${productId}/option-groups`,
    { groups },
  );
  return response.data;
};

// --- Add-ons (Wave 4.6 Catalog Depth) ---

export interface AddonPayload {
  addonProductId: string;
  isRequired?: boolean;
}

export const getProductAddons = async (
  productId: string,
): Promise<ApiResponse<{ addons: ProductAddonLink[] }>> => {
  const response = await api.get<ApiResponse<{ addons: ProductAddonLink[] }>>(
    `/products/${productId}/addons`,
  );
  return response.data;
};

/** Replace the product's full add-on set in a single call. */
export const replaceProductAddons = async (
  productId: string,
  addons: AddonPayload[],
): Promise<ApiResponse<{ addons: ProductAddonLink[] }>> => {
  const response = await api.put<ApiResponse<{ addons: ProductAddonLink[] }>>(
    `/products/${productId}/addons`,
    { addons },
  );
  return response.data;
};

// --- Features ---

export interface CreateFeaturePayload {
  feature_name: string;
  feature_value: string;
  feature_type: ProductFeatureType;
  sort_order?: number;
}

export const addProductFeature = async (productId: string, data: CreateFeaturePayload): Promise<ApiResponse<ProductFeature>> => {
  const response = await api.post<ApiResponse<ProductFeature>>(`/products/${productId}/features`, data);
  return response.data;
};

export interface UpdateFeaturePayload {
  feature_name?: string;
  feature_value?: string;
  feature_type?: ProductFeatureType;
  sort_order?: number;
}

export const updateProductFeature = async (productId: string, featureId: string, data: UpdateFeaturePayload): Promise<ApiResponse<ProductFeature>> => {
  const response = await api.put<ApiResponse<ProductFeature>>(`/products/${productId}/features/${featureId}`, data);
  return response.data;
};

export const deleteProductFeature = async (productId: string, featureId: string): Promise<ApiResponse<{ message: string }>> => {
  const response = await api.delete<ApiResponse<{ message: string }>>(`/products/${productId}/features/${featureId}`);
  return response.data;
};

// ============================================================================
// Storefront API prefix (Wave 4.7a)
//
// The public storefront resolves the merchant from the hostname and hits
// `/cart`, `/checkout`, `/public/products`. The authenticated in-dashboard
// Store resolves the merchant from the logged-in user and hits the mirror
// routes under `/store` (`/store/cart`, `/store/checkout`, `/store/products`).
//
// A single module-level prefix lets the cart slice + checkout helpers target
// either surface. The in-dashboard Store route subtree sets it on mount and
// clears it on unmount; the public storefront leaves it at '' (default).
// ============================================================================

let storefrontApiPrefix = '';
export const setStorefrontApiPrefix = (prefix: string): void => { storefrontApiPrefix = prefix; };
export const getStorefrontApiPrefix = (): string => storefrontApiPrefix;

// ============================================================================
// Cart API (Phase 2)
// ============================================================================

export const createCart = () =>
  api.post(`${storefrontApiPrefix}/cart`);

export const getCart = (token: string) =>
  api.get(`${storefrontApiPrefix}/cart`, { headers: { 'X-Cart-Token': token } });

export const addCartItem = (token: string, data: {
  productId: string;
  billingCycle: string;
  quantity?: number;
}) =>
  api.post(`${storefrontApiPrefix}/cart/items`, data, { headers: { 'X-Cart-Token': token } });

export const updateCartItem = (token: string, itemId: string, data: {
  quantity?: number;
  billingCycle?: string;
}) =>
  api.put(`${storefrontApiPrefix}/cart/items/${itemId}`, data, { headers: { 'X-Cart-Token': token } });

export const removeCartItem = (token: string, itemId: string) =>
  api.delete(`${storefrontApiPrefix}/cart/items/${itemId}`, { headers: { 'X-Cart-Token': token } });

// Guest→user merge is public-storefront only (no prefix).
export const mergeCart = (guestToken: string) =>
  api.post('/cart/merge', { guestToken });

// ============================================================================
// Checkout API (Phase 2)
// ============================================================================

export const createCheckoutSession = (cartToken: string, couponCode?: string) =>
  api.post(`${storefrontApiPrefix}/checkout`, { cartToken, couponCode });

export const setCheckoutCustomer = (checkoutId: string, data: {
  email: string;
  firstName: string;
  lastName: string;
  phone?: string;
}) =>
  api.put(`${storefrontApiPrefix}/checkout/${checkoutId}/customer`, data);

export const initiatePayment = (
  checkoutId: string,
  idempotencyKey: string,
) =>
  api.post(`${storefrontApiPrefix}/checkout/${checkoutId}/pay`, {}, {
    headers: { 'Idempotency-Key': idempotencyKey },
  });

export const getCheckoutStatus = (checkoutId: string) =>
  api.get(`${storefrontApiPrefix}/checkout/${checkoutId}/status`);

// Return-side verification fallback — server checks PayFast directly (missing ITN).
export const verifyCheckout = (checkoutId: string) =>
  api.post(`${storefrontApiPrefix}/checkout/${checkoutId}/verify`, {});

// ============================================================================
// Export default api instance for direct use
// ============================================================================

export default api;
